<!-- Read-only display of a submitted multi-select answer, marking each option as selected or not. -->

<script lang="ts" setup>
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { Check, X } from 'lucide-vue-next';
import { computed } from 'vue';

import type { MultiSelectInputConfig, MultiSelectInputValue } from '@sigma/common/forms/MultiSelectInput.js';

import * as l from './ChatMultiSelectAnswer.loc.js';

interface Props {
  /** The multi-select input configuration with available options. */
  config: MultiSelectInputConfig;
  /** The multi-select answer value to display. */
  value: MultiSelectInputValue;
}

interface AnswerOption {
  text: string;
  selected: boolean;
}

const props = defineProps<Props>();

const t = useTranslate();

const answerOptions = computed<AnswerOption[]>(() => {
  const options: AnswerOption[] = [];

  if (props.value.options.length === 0) {
    if (props.config.options) {
      for (const option of props.config.options) {
        if (option) {
          options.push({ text: option, selected: false });
        }
      }
    }
    if (props.config.noneOption) {
      options.push({ text: props.config.noneOption, selected: true });
    }
  } else {
    const selected = new Set(props.value.options);
    if (props.config.options) {
      for (const option of props.config.options) {
        if (option) {
          options.push({ text: option, selected: selected.has(option) });
        }
      }
    }
    for (const opt of props.value.options) {
      if (!props.config.options?.includes(opt)) {
        options.push({ text: opt, selected: true });
      }
    }
  }

  return options;
});
</script>

<template>
  <div :class="css.multiSelectOptions">
    <div
      v-for="option in answerOptions"
      :key="option.text"
      :class="[css.answerOption, option.selected && css.answerSelected]"
    >
      <div :class="css.answerCheckbox">
        <Check
          v-if="option.selected"
          :class="css.answerIcon"
        />
        <X
          v-else
          :class="css.answerIcon"
        />
        <span :class="css.screenReaderOnly">{{ option.selected ? t(l.selected) : t(l.notSelected) }}</span>
      </div>
      {{ option.text }}
    </div>
  </div>
</template>

<style lang="scss" module="css">
.multiSelectOptions {
  display: flex;
  flex-direction: column;
  gap: $spacing-04;
}

.answerOption {
  display: flex;
  align-items: flex-start;
  gap: $spacing-08;
  color: $color-gray-600;
  --icon-color: #{$color-red-500};
}

.answerSelected {
  color: $color-text;
  --icon-color: #{$color-green-500};
}

.answerCheckbox {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $color-white;
  width: 20px;
  height: 20px;
  margin-top: $spacing-02;
  border-radius: $border-radius-04;
  flex-shrink: 0;
}

.answerIcon {
  width: 12px;
  height: 12px;
  stroke-width: 3;
  color: var(--icon-color);
}

.screenReaderOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
